import type { TaxLotsSummary } from '../api/client'

/** $0.01 tolerance — both sides originate from the same materialized math
 *  (compute_year_aggregate), so anything past a cent is a genuine drift,
 *  not float noise. */
export const RECONCILE_TOLERANCE = 0.01

// A hair of slack past the stated tolerance absorbs binary floating-point
// representation error (e.g. 0.1 + 0.2 !== 0.3) without weakening the
// $0.01 contract in practice.
const EPSILON = 1e-6

export interface CardFigures {
  reportable: number
  // Nullable: the card falls back to `reportable - washDeferred` (which
  // double-counts recovered deferrals) when the pre-042 API doesn't send
  // `economic_net_gain_loss`. Pass the RAW field (null when absent) here,
  // not the fallback-computed value — reconciling against the card's own
  // documented-as-wrong fallback would alarm on every pre-042 row. `null`
  // skips the economic leg of the comparison entirely (minor 12).
  economic: number | null
  deferredNet: number
}

export interface ReconcileDeltas {
  reportable: number
  // null when the economic leg was skipped (see CardFigures.economic).
  economic: number | null
  deferredNet: number
}

export interface ReconcileResult {
  ok: boolean
  deltas: ReconcileDeltas
}

/**
 * Compares the /tax/lots/closed endpoint's `summary` block (money fields are
 * bare `str(float)`, e.g. "-1830.5" — NOT 2-decimal-padded) against the
 * TaxSummaryCard's already-fetched headline figures. Both are computed by
 * the same function (compute_year_aggregate) so they should always match;
 * a mismatch past the $0.01 tolerance is a genuine data-integrity tripwire,
 * not noise — see spec §9.
 */
export function reconcile(
  lotsSummary: TaxLotsSummary,
  cardFigures: CardFigures
): ReconcileResult {
  const lotsReportable = Number(lotsSummary.reportable_net)
  const lotsEconomic = Number(lotsSummary.economic_net)
  const lotsDeferredNet =
    Number(lotsSummary.wash_disallowed_total) - Number(lotsSummary.wash_recovered_total)

  const economicDelta =
    cardFigures.economic === null ? null : lotsEconomic - cardFigures.economic

  const deltas: ReconcileDeltas = {
    reportable: lotsReportable - cardFigures.reportable,
    economic: economicDelta,
    deferredNet: lotsDeferredNet - cardFigures.deferredNet,
  }

  const checked = [deltas.reportable, deltas.deferredNet]
  if (economicDelta !== null) checked.push(economicDelta)
  const ok = checked.every((d) => Math.abs(d) <= RECONCILE_TOLERANCE + EPSILON)

  return { ok, deltas }
}
